
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  refundPolicies,
  refundProcess,
  specialCases,
  refundMethods,
  importantNotes,
} from '../../mocks/refundPolicy';

export default function RefundPolicyPage() {
  const [calcAmount, setCalcAmount] = useState('');
  const [calcDays, setCalcDays] = useState('72+');
  const [calcResult, setCalcResult] = useState<number | null>(null);

  const handleCalc = () => {
    const amount = parseFloat(calcAmount);
    if (isNaN(amount) || amount <= 0) return;
    let rate = 0;
    if (calcDays === '72+') rate = 1;
    else if (calcDays === '48-72') rate = 0.7;
    else if (calcDays === '24-48') rate = 0.5;
    else rate = 0;
    setCalcResult(Math.round(amount * rate));
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://public.readdy.ai/ai/img_res/8d0e19d0-7bfb-4b2d-af00-cea6f100e1c2.png"
                alt="봉구 투어 로고"
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold text-gray-900">봉구 투어</span>
            </Link>
            <div className="flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                홈
              </Link>
              <Link to="/#tours" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                투어 상품
              </Link>
              <Link to="/faq" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                자주 묻는 질문
              </Link>
              <Link to="/refund-policy" className="text-sm font-medium text-[#00BCD4] cursor-pointer whitespace-nowrap">
                환불 정책
              </Link>
              <Link to="/#contact" className="px-6 py-2.5 bg-[#1A237E] text-white text-sm font-semibold rounded-full hover:bg-[#0D47A1] transition-colors cursor-pointer whitespace-nowrap">
                문의하기
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-80 h-80 bg-[#00BCD4] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#FF9800] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E0F7FA] rounded-full mb-6">
            <i className="ri-shield-check-line text-[#00BCD4]"></i>
            <span className="text-sm font-semibold text-[#00BCD4]">투명한 환불 정책</span>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4 leading-tight">
            환불 정책 안내
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            봉구 투어는 고객님의 권리를 최우선으로 생각합니다.<br />
            투명하고 공정한 환불 절차를 안내해 드립니다.
          </p>
          <p className="text-sm text-gray-400 mt-6">최종 업데이트: 2025년 1월</p>
        </div>
      </section>

      {/* Refund Rate Cards */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">취소 시점별 환불 비율</h2>
            <p className="text-gray-600">투어 출발일 기준으로 취소 시점에 따라 환불 비율이 달라집니다</p>
          </div>

          <div className="grid grid-cols-4 gap-5">
            {refundPolicies.map((policy) => (
              <div
                key={policy.id}
                className={`relative rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl cursor-default ${
                  policy.highlight
                    ? 'bg-[#00BCD4] text-white shadow-lg'
                    : 'bg-white text-gray-900 shadow-sm border border-gray-100'
                }`}
              >
                {policy.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-[#FF6B6B] text-white text-xs font-bold rounded-full whitespace-nowrap">추천</span>
                  </div>
                )}
                <div className={`text-5xl font-bold mb-3 ${policy.highlight ? 'text-white' : 'text-[#00BCD4]'}`}>
                  {policy.percentage}%
                </div>
                <div className={`text-xs font-medium mb-2 ${policy.highlight ? 'text-white/70' : 'text-gray-400'}`}>환불</div>
                <h3 className="text-base font-bold mb-3 leading-snug">{policy.title}</h3>
                <p className={`text-sm leading-relaxed ${policy.highlight ? 'text-white/85' : 'text-gray-600'}`}>
                  {policy.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Refund Process Timeline */}
      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">환불 처리 절차</h2>
            <p className="text-gray-600">간편하고 빠른 4단계 환불 프로세스</p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute top-16 left-0 right-0 h-0.5 bg-gray-200 hidden lg:block"></div>
            <div className="grid grid-cols-4 gap-6">
              {refundProcess.map((step, idx) => (
                <div key={step.step} className="relative text-center">
                  <div className="relative z-10 w-14 h-14 mx-auto mb-5 bg-[#00BCD4] rounded-2xl flex items-center justify-center shadow-lg shadow-[#00BCD4]/20">
                    <i className={`${step.icon} text-white text-2xl`}></i>
                  </div>
                  {idx < refundProcess.length - 1 && (
                    <div className="absolute top-7 left-[calc(50%+28px)] w-[calc(100%-56px)] hidden lg:flex items-center justify-center">
                      <i className="ri-arrow-right-line text-gray-300 text-xl"></i>
                    </div>
                  )}
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                    <div className="text-xs font-bold text-[#00BCD4] mb-2">STEP {step.step}</div>
                    <h4 className="text-base font-bold text-gray-900 mb-2">{step.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">{step.description}</p>
                    <span className="inline-block px-3 py-1 bg-[#E0F7FA] text-[#00BCD4] text-xs font-semibold rounded-full whitespace-nowrap">
                      {step.duration}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content: Two Column */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto flex gap-8">
          {/* Left: Special Cases */}
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">특별 환불 사유</h2>
            <p className="text-gray-600 mb-8">아래 사유에 해당하는 경우 별도의 환불 규정이 적용됩니다</p>

            <div className="grid grid-cols-2 gap-5">
              {specialCases.map((item) => (
                <div
                  key={item.id}
                  className="rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-default"
                  style={{ backgroundColor: item.color }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${item.iconColor}20` }}
                  >
                    <i className={`${item.icon} text-2xl`} style={{ color: item.iconColor }}></i>
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Sidebar */}
          <div className="w-80 flex-shrink-0">
            {/* Refund Calculator */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6 sticky top-28">
              <h3 className="text-lg font-bold text-gray-900 mb-1">예상 환불액 계산기</h3>
              <p className="text-xs text-gray-500 mb-5">투어 금액과 취소 시점을 선택하세요</p>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">투어 금액 (₱)</label>
                  <input
                    type="number"
                    value={calcAmount}
                    onChange={(e) => { setCalcAmount(e.target.value); setCalcResult(null); }}
                    placeholder="예: 2500"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#00BCD4] text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">취소 시점</label>
                  <select
                    value={calcDays}
                    onChange={(e) => { setCalcDays(e.target.value); setCalcResult(null); }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#00BCD4] text-sm bg-white cursor-pointer"
                  >
                    <option value="72+">72시간 이전 (100% 환불)</option>
                    <option value="48-72">48~72시간 전 (70% 환불)</option>
                    <option value="24-48">24~48시간 전 (50% 환불)</option>
                    <option value="24-">24시간 이내 (환불 불가)</option>
                  </select>
                </div>
                <button
                  onClick={handleCalc}
                  className="w-full py-3 bg-[#00BCD4] text-white font-semibold rounded-xl hover:bg-[#00ACC1] transition-colors cursor-pointer whitespace-nowrap"
                >
                  계산하기
                </button>
                {calcResult !== null && (
                  <div className="bg-[#E0F7FA] rounded-xl p-4 text-center">
                    <div className="text-xs text-gray-500 mb-1">예상 환불 금액</div>
                    <div className="text-3xl font-bold text-[#00BCD4]">₱{calcResult.toLocaleString()}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Refund Methods */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">결제 수단별 환불 기간</h3>
              <div className="space-y-4">
                {refundMethods.map((m, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#E0F7FA] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${m.icon} text-[#00BCD4] text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900">{m.method}</div>
                      <div className="text-xs text-gray-500">{m.duration}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Card */}
            <div className="bg-[#1A237E] rounded-2xl p-6 text-white">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">환불 문의</h3>
              <p className="text-sm text-white/70 mb-4 leading-relaxed">환불 관련 궁금한 점이 있으시면 언제든 연락주세요.</p>
              <div className="space-y-2 mb-5">
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-phone-line text-[#00BCD4]"></i>
                  <span>+63 917 123 4567</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-kakao-talk-fill text-[#FFE500]"></i>
                  <span>@bonggutour</span>
                </div>
              </div>
              <div className="text-xs text-white/50">평일 09:00 ~ 18:00 (한국 시간)</div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl p-10 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#FFF3E0] rounded-lg flex items-center justify-center">
                <i className="ri-error-warning-line text-[#FF9800] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">유의 사항</h3>
            </div>
            <div className="grid grid-cols-2 gap-x-10 gap-y-4">
              {importantNotes.map((note, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#E0F7FA] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-[#00BCD4]">{idx + 1}</span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/11dcaacb7d75473eca8bc95e26c61ade.jpg"
              alt="세부 바다 석양"
              className="w-full h-[280px] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A237E]/85 via-[#1A237E]/70 to-[#00BCD4]/50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <h3 className="text-3xl font-bold mb-3">환불 신청이 필요하신가요?</h3>
                <p className="text-white/80 mb-8 text-base">카카오톡 또는 이메일로 간편하게 신청하세요</p>
                <div className="flex items-center justify-center gap-4">
                  <Link
                    to="/#contact"
                    className="px-7 py-3 bg-white text-[#1A237E] font-semibold rounded-full hover:bg-gray-100 transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-chat-3-line"></i>
                    환불 신청하기
                  </Link>
                  <Link
                    to="/faq"
                    className="px-7 py-3 bg-[#00BCD4] text-white font-semibold rounded-full hover:bg-[#00ACC1] transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-question-line"></i>
                    자주 묻는 질문
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl p-12">
            <div className="grid grid-cols-5 gap-12 mb-12">
              <div className="col-span-1">
                <Link to="/" className="flex items-center gap-2 mb-4 cursor-pointer">
                  <img
                    src="https://public.readdy.ai/ai/img_res/8d0e19d0-7bfb-4b2d-af00-cea6f100e1c2.png"
                    alt="봉구 투어"
                    className="h-10 w-auto"
                  />
                  <span className="text-xl font-bold text-gray-900">봉구 투어</span>
                </Link>
                <p className="text-sm text-gray-600 leading-relaxed">
                  필리핀 세부 현지 여행 전문<br />안전한 투어 보장
                </p>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4"><a href="/#tours">TOURS</a></h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/tour/oslob-whale-shark" className="hover:text-[#00BCD4] transition-colors cursor-pointer">고래상어 투어</Link></li>
                  <li><Link to="/tour/kawasan-falls" className="hover:text-[#00BCD4] transition-colors cursor-pointer">카와산 폭포</Link></li>
                  <li><Link to="/tour/bohol-day-tour" className="hover:text-[#00BCD4] transition-colors cursor-pointer">보홀 당일 투어</Link></li>
                  <li><Link to="/tour/sunset-cruise" className="hover:text-[#00BCD4] transition-colors cursor-pointer">선셋 크루즈</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4">COMPANY</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/about" className="hover:text-[#00BCD4] transition-colors cursor-pointer">회사 소개</Link></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">가이드 소개</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">파트너십</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">채용 정보</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4">SUPPORT</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/faq" className="hover:text-[#00BCD4] transition-colors cursor-pointer">자주 묻는 질문</Link></li>
                  <li><Link to="/#contact" className="hover:text-[#00BCD4] transition-colors cursor-pointer">예약 문의</Link></li>
                  <li><Link to="/refund-policy" className="text-[#00BCD4] font-medium cursor-pointer">환불 정책</Link></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">이용 약관</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4">FOLLOW US</h4>
                <div className="flex gap-3">
                  <a href="#" className="w-10 h-10 bg-[#FFE500] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
                    <i className="ri-kakao-talk-fill text-[#3C1E1E] text-xl"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#515BD4] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
                    <i className="ri-instagram-line text-white text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-8 flex items-center justify-between">
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <span>&copy; 2025 봉구 투어. All rights reserved.</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#00BCD4] transition-colors cursor-pointer">Web Design</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
